// frontend/src/components/DataLineage.jsx
import React, { useState, useMemo, useRef } from 'react';
import { X, Download, ChevronDown, ChevronRight, GitCommitHorizontal } from 'lucide-react';
import { useLanguageStore } from '../store/useLanguageStore';
import { TRANSFORM_RULES, getTransformRule } from '../utils/transformRules';

// ══════════════════════════════════════════════════════════════
//  CATEGORÍAS — derivadas de transformRules.js (fuente única)
//
//  Nunca hardcodear reglas aquí. Si se agrega una regla nueva
//  en transformRules.js, el lineage la recoge automáticamente.
// ══════════════════════════════════════════════════════════════

// Mapa id → category derivado dinámicamente
const RULE_TO_CATEGORY = Object.fromEntries(
  TRANSFORM_RULES.map(r => [r.id, r.category])
);

// Colores por categoría canónica de transformRules.js
const CATEGORY_COLORS = {
  text: { bg: '#dcfce7', border: '#86efac', text: '#166534', dot: '#22c55e' },
  number: { bg: '#dbeafe', border: '#93c5fd', text: '#1e40af', dot: '#3b82f6' },
  date: { bg: '#f3e8ff', border: '#d8b4fe', text: '#6b21a8', dot: '#a855f7' },
  cond: { bg: '#ffedd5', border: '#fdba74', text: '#9a3412', dot: '#f97316' },
  fixed: { bg: '#f1f5f9', border: '#cbd5e1', text: '#475569', dot: '#94a3b8' },
  security: { bg: '#fef3c7', border: '#fcd34d', text: '#92400e', dot: '#f59e0b' },
  lookup: { bg: '#fce7f3', border: '#f9a8d4', text: '#9d174d', dot: '#ec4899' },
  unknown: { bg: '#f1f5f9', border: '#cbd5e1', text: '#475569', dot: '#94a3b8' },
};

function getRuleColor(ruleType) {
  const cat = RULE_TO_CATEGORY[ruleType] || 'unknown';
  return CATEGORY_COLORS[cat] || CATEGORY_COLORS.unknown;
}

function getRuleCategory(ruleType) {
  return RULE_TO_CATEGORY[ruleType] || 'unknown';
}

// ══════════════════════════════════════════════════════════════
//  CONSTANTES DE LAYOUT SVG
//
//  Layout: DESTINO (izq) ← REGLAS (centro) ← ORIGEN (der)
//  Consistente con StepMapping donde destino está a la izquierda
//
//  v3: columnas compactas (640px), filas unificadas,
//  categorías derivadas de transformRules.js.
// ══════════════════════════════════════════════════════════════
const VB_W = 520;   // viewBox width (el SVG usa width="100%")
const NODE_W = 115;
const NODE_H = 26;
const ROW_PAD = 5;
const GROUP_HDR_H = 22;
const GROUP_GAP = 10;
const RULE_W = 68;
const RULE_H = 17;
const RULE_GAP = 2;
const MARGIN_X = 6;
const MARGIN_TOP = 38;

// Columnas X — compactas, sin espacio muerto
const TGT_X = MARGIN_X;                            // 6
const SRC_X = VB_W - MARGIN_X - NODE_W;             // 399
const TGT_RX = TGT_X + NODE_W;                      // 121
const SRC_LX = SRC_X;                                // 399
const CENTER_X = (TGT_RX + SRC_LX) / 2;               // 260
// Gap target→reglas: ~105px. Gap reglas→source: ~105px.

// ══════════════════════════════════════════════════════════════
//  HELPERS
// ══════════════════════════════════════════════════════════════
const truncate = (s, n = 18) => (s && s.length > n ? s.slice(0, n - 1) + '…' : (s || ''));
const stripPrefix = (field) => field?.includes('.') ? field.split('.').pop() : (field || '');
const getRuleLabel = (ruleType, t) => {
  // 1. Intentar i18n (wizard.mapping.rule_names.${id})
  const i18nKey = `wizard.mapping.rule_names.${ruleType}`;
  const i18nLabel = t(i18nKey);
  if (i18nLabel && i18nLabel !== i18nKey) return i18nLabel;
  // 2. Fallback: label del catálogo transformRules.js
  const def = getTransformRule(ruleType);
  return def?.label || ruleType;
};

// ══════════════════════════════════════════════════════════════
//  MOTOR DE LAYOUT v3 — filas unificadas
//
//  Cada mapping = una FILA. Altura = max(NODE_H, ruleStackH) + ROW_PAD.
//  Garantiza cero overlap entre reglas de campos adyacentes.
// ══════════════════════════════════════════════════════════════
function computeLayout(mappings) {
  const active = mappings.filter(m => !m.ignored);
  const ignored = mappings.filter(m => m.ignored);

  // ── Agrupar destinos por tabla ──────────────────────────────
  const tgtGroups = new Map();
  for (const m of active) {
    const tf = m.targetField || '';
    const tbl = tf.includes('.') ? tf.split('.')[0] : '_default';
    if (!tgtGroups.has(tbl)) tgtGroups.set(tbl, []);
    tgtGroups.get(tbl).push(m);
  }

  // ── Agrupar orígenes por tabla (sin duplicados) ─────────────
  const srcGroups = new Map();
  const srcSeen = new Set();
  for (const m of active) {
    const sf = m.sourceField || '';
    if (!sf || m.isFixed || srcSeen.has(sf)) continue;
    srcSeen.add(sf);
    const tbl = sf.includes('.') ? sf.split('.')[0] : '_default';
    if (!srcGroups.has(tbl)) srcGroups.set(tbl, []);
    srcGroups.get(tbl).push(sf);
  }

  // ── Calcular filas unificadas ───────────────────────────────
  const rows = [];
  let cursorY = MARGIN_TOP;
  const tgtGroupHeaders = [];

  for (const [table, items] of tgtGroups) {
    const showHeader = table !== '_default' || tgtGroups.size > 1;
    if (showHeader) {
      tgtGroupHeaders.push({ table, y: cursorY });
      cursorY += GROUP_HDR_H;
    }

    for (const m of items) {
      const nRules = (m.transformations || []).length;
      const ruleStackH = nRules > 0
        ? nRules * RULE_H + (nRules - 1) * RULE_GAP
        : 0;
      const rowH = Math.max(NODE_H, ruleStackH) + ROW_PAD;
      const midY = cursorY + rowH / 2;

      const ruleItems = (m.transformations || []).map((rule, ri) => {
        const ruleY = midY - ruleStackH / 2 + ri * (RULE_H + RULE_GAP);
        return {
          type: rule.type,
          x: CENTER_X - RULE_W / 2,
          y: ruleY,
          cy: ruleY + RULE_H / 2,
          color: getRuleColor(rule.type),
        };
      });

      rows.push({
        m,
        tgtY: midY - NODE_H / 2,
        tgtCY: midY,
        rowH,
        rules: ruleItems,
      });

      cursorY += rowH;
    }

    cursorY += GROUP_GAP;
  }

  // ── Posiciones source ───────────────────────────────────────
  const srcPositions = new Map();
  let srcCursorY = MARGIN_TOP;
  const srcGroupHeaders = [];

  for (const [table, fields] of srcGroups) {
    const showHeader = table !== '_default' || srcGroups.size > 1;
    if (showHeader) {
      srcGroupHeaders.push({ table, y: srcCursorY });
      srcCursorY += GROUP_HDR_H;
    }

    for (const sf of fields) {
      srcPositions.set(sf, {
        x: SRC_X,
        y: srcCursorY,
        cy: srcCursorY + NODE_H / 2,
      });
      srcCursorY += NODE_H + ROW_PAD;
    }

    srcCursorY += GROUP_GAP;
  }

  // ── Ignorados ───────────────────────────────────────────────
  const ignoredPositions = [];
  if (ignored.length > 0) {
    cursorY += 4;
    for (const m of ignored) {
      ignoredPositions.push({
        field: m.targetField,
        x: TGT_X,
        y: cursorY,
        cy: cursorY + NODE_H / 2,
      });
      cursorY += NODE_H + ROW_PAD;
    }
  }

  const totalH = Math.max(cursorY, srcCursorY) + 20;

  return {
    rows, srcPositions, tgtGroupHeaders, srcGroupHeaders,
    ignoredPositions, totalH,
  };
}

// ══════════════════════════════════════════════════════════════
//  COMPONENTE SVG — GRAFO DE LINEAGE
// ══════════════════════════════════════════════════════════════
function LineageSVG({ mappings, sourceEngine, targetEngine, t, svgRef }) {
  const layout = useMemo(() => computeLayout(mappings), [mappings]);
  const {
    rows, srcPositions, tgtGroupHeaders, srcGroupHeaders,
    ignoredPositions, totalH,
  } = layout;

  const srcLabel = t('lineage.source_fields') || 'ORIGEN';
  const tgtLabel = t('lineage.target_fields') || 'DESTINO';

  return (
    <svg
      ref={svgRef}
      width="100%"
      height={totalH}
      viewBox={`0 0 ${VB_W} ${totalH}`}
      preserveAspectRatio="xMidYMin meet"
      className="block"
      aria-label="Data Lineage"
      style={{ background: 'transparent', maxWidth: `${VB_W}px` }}
    >
      {/* ── Defs: marcadores de flecha ── */}
      <defs>
        <marker id="arrow-lineage" markerWidth="7" markerHeight="5" refX="6" refY="2.5" orient="auto">
          <polygon points="0 0, 7 2.5, 0 5" fill="#94a3b8" />
        </marker>
        <marker id="arrow-rule" markerWidth="7" markerHeight="5" refX="6" refY="2.5" orient="auto">
          <polygon points="0 0, 7 2.5, 0 5" fill="#6366f1" />
        </marker>
      </defs>

      {/* ── Labels de columna ── */}
      <text x={TGT_X + NODE_W / 2} y={16} textAnchor="middle"
        fontSize="9" fontWeight="800" fill="#10b981" letterSpacing="1.2"
      >{tgtLabel}</text>
      {targetEngine && (
        <text x={TGT_X + NODE_W / 2} y={28} textAnchor="middle"
          fontSize="8" fontWeight="600" fill="#6ee7b7"
        >{targetEngine}</text>
      )}

      <text x={SRC_X + NODE_W / 2} y={16} textAnchor="middle"
        fontSize="9" fontWeight="800" fill="#6366f1" letterSpacing="1.2"
      >{srcLabel}</text>
      {sourceEngine && (
        <text x={SRC_X + NODE_W / 2} y={28} textAnchor="middle"
          fontSize="8" fontWeight="600" fill="#a5b4fc"
        >{sourceEngine}</text>
      )}

      {/* Etiqueta central */}
      <text x={CENTER_X} y={16} textAnchor="middle"
        fontSize="8" fontWeight="700" fill="#94a3b8" letterSpacing="0.8"
      >{t('lineage.rules_applied') || 'REGLAS'}</text>

      {/* ── Headers de grupo destino ── */}
      {tgtGroupHeaders.map((g, i) => (
        <g key={`tgh-${i}`}>
          <rect x={TGT_X - 2} y={g.y} width={NODE_W + 4} height={GROUP_HDR_H - 3}
            rx="5" fill="#d1fae5" opacity="0.5" />
          <text x={TGT_X + NODE_W / 2} y={g.y + GROUP_HDR_H / 2}
            textAnchor="middle" fontSize="8" fontWeight="800" fill="#065f46"
          >{truncate(g.table, 20)}</text>
        </g>
      ))}

      {/* ── Headers de grupo origen ── */}
      {srcGroupHeaders.map((g, i) => (
        <g key={`sgh-${i}`}>
          <rect x={SRC_X - 2} y={g.y} width={NODE_W + 4} height={GROUP_HDR_H - 3}
            rx="5" fill="#e0e7ff" opacity="0.5" />
          <text x={SRC_X + NODE_W / 2} y={g.y + GROUP_HDR_H / 2}
            textAnchor="middle" fontSize="8" fontWeight="800" fill="#312e81"
          >{truncate(g.table, 20)}</text>
        </g>
      ))}

      {/* ── Filas: conexiones + nodos ── */}
      {rows.map((row, idx) => {
        const { m, tgtY, tgtCY, rules } = row;
        const srcPos = srcPositions.get(m.sourceField);
        const hasRules = rules.length > 0;

        return (
          <g key={`row-${idx}`}>
            {/* ── Nodo DESTINO (izquierda) ── */}
            <g className="cursor-default">
              <title>{stripPrefix(m.targetField)}</title>
              <rect x={TGT_X} y={tgtY} width={NODE_W} height={NODE_H} rx="5"
                fill="#ecfdf5" stroke="#a7f3d0" strokeWidth="1" />
              <rect x={TGT_X + NODE_W - 3} y={tgtY} width={3} height={NODE_H} rx="1.5" fill="#10b981" />
              <text x={TGT_X + 8} y={tgtCY + 3.5}
                fontSize="9" fontWeight="600" fill="#065f46"
              >{truncate(stripPrefix(m.targetField), 15)}</text>
            </g>

            {/* ── Caso: valor fijo sin source ── */}
            {m.isFixed && !srcPos && (
              <>
                <rect
                  x={CENTER_X - RULE_W / 2} y={tgtCY - RULE_H / 2}
                  width={RULE_W} height={RULE_H} rx="4"
                  fill={CATEGORY_COLORS.fixed.bg} stroke={CATEGORY_COLORS.fixed.border} strokeWidth="1"
                />
                <text x={CENTER_X} y={tgtCY + 3} textAnchor="middle"
                  fontSize="7.5" fontWeight="700" fill={CATEGORY_COLORS.fixed.text}
                >{truncate(m.fixedValue != null ? String(m.fixedValue) : 'FIXED', 10)}</text>
                <line
                  x1={CENTER_X - RULE_W / 2} y1={tgtCY}
                  x2={TGT_RX + 2} y2={tgtCY}
                  stroke="#cbd5e1" strokeWidth="1" strokeDasharray="4 3"
                  markerEnd="url(#arrow-lineage)"
                />
              </>
            )}

            {/* ── Caso: mapeo directo sin reglas ── */}
            {srcPos && !hasRules && (
              <path
                d={`M ${SRC_LX} ${srcPos.cy} C ${SRC_LX - 20} ${srcPos.cy} ${TGT_RX + 20} ${tgtCY} ${TGT_RX} ${tgtCY}`}
                fill="none" stroke="#cbd5e1" strokeWidth="1"
                markerEnd="url(#arrow-lineage)"
              />
            )}

            {/* ── Caso: mapeo con reglas ── */}
            {srcPos && hasRules && (() => {
              const firstRule = rules[0];
              const lastRule = rules[rules.length - 1];
              return (
                <>
                  <path
                    d={`M ${SRC_LX} ${srcPos.cy} C ${SRC_LX - 20} ${srcPos.cy} ${firstRule.x + RULE_W + 20} ${firstRule.cy} ${firstRule.x + RULE_W} ${firstRule.cy}`}
                    fill="none" stroke="#a5b4fc" strokeWidth="1" markerEnd="url(#arrow-rule)"
                  />
                  <path
                    d={`M ${lastRule.x} ${lastRule.cy} C ${lastRule.x - 20} ${lastRule.cy} ${TGT_RX + 20} ${tgtCY} ${TGT_RX} ${tgtCY}`}
                    fill="none" stroke="#a5b4fc" strokeWidth="1" markerEnd="url(#arrow-rule)"
                  />
                </>
              );
            })()}

            {/* ── Nodos de reglas ── */}
            {rules.map((rule, ri) => (
              <g key={`rule-${idx}-${ri}`} className="cursor-help">
                <title>{getRuleLabel(rule.type, t)}</title>
                <rect
                  x={rule.x} y={rule.y} width={RULE_W} height={RULE_H} rx="3.5"
                  fill={rule.color.bg} stroke={rule.color.border} strokeWidth="0.8"
                />
                <circle cx={rule.x + 8} cy={rule.cy} r="2.5" fill={rule.color.dot} />
                <text x={rule.x + 14} y={rule.cy + 3}
                  fontSize="7.5" fontWeight="700" fill={rule.color.text}
                >{truncate(rule.type, 10)}</text>
              </g>
            ))}
          </g>
        );
      })}

      {/* ── Nodos ORIGEN (derecha) — z-index superior ── */}
      {[...srcPositions.entries()].map(([field, pos]) => (
        <g key={`src-${field}`} className="cursor-default">
          <title>{stripPrefix(field)}</title>
          <rect x={pos.x} y={pos.y} width={NODE_W} height={NODE_H} rx="5"
            fill="#eef2ff" stroke="#c7d2fe" strokeWidth="1" />
          <rect x={pos.x} y={pos.y} width={3} height={NODE_H} rx="1.5" fill="#6366f1" />
          <text x={pos.x + 10} y={pos.cy + 3.5}
            fontSize="9" fontWeight="600" fill="#312e81"
          >{truncate(stripPrefix(field), 15)}</text>
        </g>
      ))}

      {/* ── Campos ignorados (gris tenue) ── */}
      {ignoredPositions.length > 0 && (
        <text x={TGT_X + NODE_W / 2} y={ignoredPositions[0].y - 6}
          textAnchor="middle" fontSize="7.5" fontWeight="700" fill="#94a3b8" letterSpacing="0.5"
        >{t('lineage.ignored_fields') || 'IGNORADOS'}</text>
      )}
      {ignoredPositions.map((pos, i) => (
        <g key={`ign-${i}`} opacity="0.4">
          <rect x={pos.x} y={pos.y} width={NODE_W} height={NODE_H} rx="5"
            fill="#f8fafc" stroke="#e2e8f0" strokeWidth="0.8" strokeDasharray="4 3" />
          <text x={pos.x + 8} y={pos.cy + 3.5}
            fontSize="9" fontWeight="500" fill="#94a3b8"
          >{truncate(stripPrefix(pos.field))}</text>
        </g>
      ))}
    </svg>
  );
}

// ══════════════════════════════════════════════════════════════
//  LEYENDA — derivada de categorías realmente usadas
// ══════════════════════════════════════════════════════════════
const LEGEND_LABELS = {
  text: { es: 'Texto', en: 'Text', pt: 'Texto' },
  number: { es: 'Numérico', en: 'Number', pt: 'Numérico' },
  date: { es: 'Fecha', en: 'Date', pt: 'Data' },
  cond: { es: 'Condicional', en: 'Conditional', pt: 'Condicional' },
  fixed: { es: 'Valor fijo', en: 'Fixed value', pt: 'Valor fixo' },
  security: { es: 'Seguridad', en: 'Security', pt: 'Segurança' },
  lookup: { es: 'Lookup', en: 'Lookup', pt: 'Lookup' },
};

function RuleLegend({ mappings, t }) {
  const usedCategories = useMemo(() => {
    const cats = new Set();
    for (const m of mappings) {
      for (const r of (m.transformations || [])) {
        cats.add(getRuleCategory(r.type));
      }
    }
    cats.delete('unknown');
    return cats;
  }, [mappings]);

  if (usedCategories.size === 0) return null;

  // Orden fijo de la leyenda
  const ORDER = ['text', 'number', 'date', 'cond', 'fixed', 'security', 'lookup'];
  const items = ORDER.filter(k => usedCategories.has(k));

  return (
    <div className="flex items-center gap-3 flex-wrap px-1">
      {items.map(key => {
        const c = CATEGORY_COLORS[key];
        const label = t(`lineage.cat_${key}`) || LEGEND_LABELS[key]?.es || key;
        return (
          <div key={key} className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c.dot }} />
            <span className="text-[10px] font-semibold" style={{ color: c.text }}>{label}</span>
          </div>
        );
      })}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  EXPORTAR SVG
// ══════════════════════════════════════════════════════════════
function downloadSVG(svgRef, fileName) {
  const svg = svgRef.current;
  if (!svg) return;

  const clone = svg.cloneNode(true);
  // Restaurar width explícito para que el SVG exportado se vea bien en editores
  clone.setAttribute('width', VB_W);

  const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  bg.setAttribute('width', '100%');
  bg.setAttribute('height', '100%');
  bg.setAttribute('fill', '#ffffff');
  clone.insertBefore(bg, clone.firstChild);

  const data = new XMLSerializer().serializeToString(clone);
  const blob = new Blob([data], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${fileName || 'lineage'}.svg`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ══════════════════════════════════════════════════════════════
//  MODAL FULLSCREEN — usado desde Dashboard
// ══════════════════════════════════════════════════════════════
export function DataLineageModal({ data, onClose }) {
  const { t } = useLanguageStore();
  const svgRef = useRef(null);

  React.useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  if (!data) return null;

  const fileName = (data.pipelineName || 'lineage').replace(/[^a-zA-Z0-9_-]/g, '_');

  return (
    <div
      className="fixed inset-0 z-[100] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100 bg-slate-50 shrink-0">
          <div>
            <h3 className="font-bold text-slate-800 flex items-center gap-2 text-sm">
              <GitCommitHorizontal className="w-4 h-4 text-indigo-600" />
              {t('lineage.title') || 'Data Lineage'}
            </h3>
            <p className="text-[11px] text-slate-400 mt-0.5">
              {data.pipelineName}
              <span className="mx-1.5">·</span>
              <span className="font-semibold text-indigo-500">{data.sourceEngine}</span>
              <span className="mx-1"> → </span>
              <span className="font-semibold text-emerald-500">{data.targetEngine}</span>
              <span className="mx-1.5">·</span>
              <span className="font-mono text-slate-400">{data.mode}</span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => downloadSVG(svgRef, `lineage_${fileName}`)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition"
              title={t('lineage.download_svg') || 'Descargar SVG'}
            >
              <Download className="w-3.5 h-3.5" />
              {t('lineage.download_svg') || 'Descargar SVG'}
            </button>
            <button onClick={onClose}
              className="p-1.5 text-slate-400 hover:text-slate-700 rounded-full hover:bg-slate-100 transition"
              title={t('common.buttons.close') || 'Cerrar'}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* SVG graph */}
        <div className="flex-1 overflow-auto p-5 bg-white">
          {/* Info banner */}
          <div className="mb-4 px-4 py-3 bg-indigo-50/60 border border-indigo-100 rounded-xl">
            <p className="text-[11px] text-indigo-700 leading-relaxed">
              <span className="font-bold">{t('lineage.info_how_to_read') || '¿Cómo leer este diagrama?'}</span>
              {' '}
              {t('lineage.info_description') || 'Los datos fluyen de derecha a izquierda: desde los campos de origen, pasan por las reglas de transformación (si las hay), y llegan a los campos de destino. Los campos en gris al final están ignorados y no se migran. Pasa el cursor sobre cualquier regla para ver su descripción completa.'}
            </p>
          </div>
          <div className="overflow-x-auto min-w-0">
            {data.mappings?.length > 0 ? (
              <LineageSVG
                mappings={data.mappings}
                sourceEngine={data.sourceEngine}
                targetEngine={data.targetEngine}
                t={t}
                svgRef={svgRef}
              />
            ) : (
              <div className="text-center py-12">
                <p className="text-sm text-slate-400 italic">
                  {t('lineage.no_data') || 'Sin datos de mapeo para este pipeline.'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Legend + Footer */}
        <div className="px-5 py-2.5 border-t border-slate-100 bg-slate-50 shrink-0 flex items-center justify-between">
          <RuleLegend mappings={data.mappings || []} t={t} />
          <span className="text-[10px] text-slate-400">
            {t('lineage.esc_hint') || 'ESC o clic fuera para cerrar'}
          </span>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  PANEL EXPANDIBLE — usado en StepReview
// ══════════════════════════════════════════════════════════════
export function DataLineagePanel({ mappings, sourceEngine, targetEngine, mode }) {
  const { t } = useLanguageStore();
  const svgRef = useRef(null);
  const [expanded, setExpanded] = useState(false);

  if (!mappings || mappings.length === 0) return null;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      <button
        onClick={() => setExpanded(prev => !prev)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-slate-50 transition"
      >
        <h3 className="font-bold text-slate-800 flex items-center text-sm">
          <GitCommitHorizontal className="w-5 h-5 mr-2 text-indigo-600" />
          {t('lineage.title') || 'Data Lineage'}
        </h3>
        {expanded
          ? <ChevronDown className="w-4 h-4 text-slate-400" />
          : <ChevronRight className="w-4 h-4 text-slate-400" />
        }
      </button>

      {expanded && (
        <div className="border-t border-slate-100">
          {/* Info banner — cómo leer el diagrama */}
          <div className="mx-6 mt-4 mb-2 px-4 py-3 bg-indigo-50/60 border border-indigo-100 rounded-xl">
            <p className="text-[11px] text-indigo-700 leading-relaxed">
              <span className="font-bold">{t('lineage.info_how_to_read') || '¿Cómo leer este diagrama?'}</span>
              {' '}
              {t('lineage.info_description') || 'Los datos fluyen de derecha a izquierda: desde los campos de origen, pasan por las reglas de transformación (si las hay), y llegan a los campos de destino. Los campos en gris al final están ignorados y no se migran. Pasa el cursor sobre cualquier regla para ver su descripción completa.'}
            </p>
          </div>

          <div className="px-6 py-2 flex justify-end">
            <button
              onClick={() => downloadSVG(svgRef, 'lineage_preview')}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition"
            >
              <Download className="w-3.5 h-3.5" />
              {t('lineage.download_svg') || 'Descargar SVG'}
            </button>
          </div>

          <div className="px-6 pb-4 overflow-x-auto">
            <LineageSVG
              mappings={mappings}
              sourceEngine={sourceEngine}
              targetEngine={targetEngine}
              t={t}
              svgRef={svgRef}
            />
          </div>

          <div className="px-6 py-3 border-t border-slate-100 bg-slate-50">
            <RuleLegend mappings={mappings} t={t} />
          </div>
        </div>
      )}
    </div>
  );
}
